/**
 * usePagination Hook
 *
 * Custom hook for client-side pagination logic.
 * Handles page state, calculations, and data slicing.
 *
 * @module domains/personnel/presentation/hooks/usePagination
 */

import { useState, useMemo, useCallback } from 'react';

/** Default number of items per page */
const DEFAULT_ITEMS_PER_PAGE = 10;

/**
 * Configuration options for usePagination hook
 */
export interface UsePaginationOptions {
  /** Number of items to display per page (default: 10) */
  itemsPerPage?: number;
  /** Initial page number (default: 1) */
  initialPage?: number;
}

/**
 * Return type for usePagination hook
 */
export interface UsePaginationResult<T> {
  /** Current active page (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Total number of items */
  totalItems: number;
  /** Items for the current page */
  paginatedData: T[];
  /** Number of items per page */
  itemsPerPage: number;
  /** Whether there is a next page */
  hasNextPage: boolean;
  /** Whether there is a previous page */
  hasPreviousPage: boolean;
  /** Go to a specific page */
  goToPage: (page: number) => void;
  /** Go to the next page */
  nextPage: () => void;
  /** Go to the previous page */
  previousPage: () => void;
  /** Reset to first page */
  resetPage: () => void;
}

/**
 * Custom hook for client-side pagination
 *
 * Provides pagination state and utilities for slicing data arrays.
 * Useful when the backend doesn't support pagination.
 *
 * @template T - Type of items in the data array
 * @param {T[]} data - Array of items to paginate
 * @param {UsePaginationOptions} options - Pagination configuration
 * @returns {UsePaginationResult<T>} Pagination state and controls
 *
 * @example
 * const { paginatedData, currentPage, totalPages, goToPage } = usePagination(roles, { itemsPerPage: 10 });
 *
 * // In search handler, reset to first page
 * const handleSearch = (query: string) => {
 *   setSearchQuery(query);
 *   resetPage();
 * };
 */
export const usePagination = <T>(data: T[], options: UsePaginationOptions = {}): UsePaginationResult<T> => {
  const { itemsPerPage = DEFAULT_ITEMS_PER_PAGE, initialPage = 1 } = options;

  const [currentPage, setCurrentPage] = useState(initialPage);

  // Calculate total items and pages
  const totalItems = data.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  // Ensure current page is within bounds when data changes
  const validCurrentPage = useMemo(() => {
    if (currentPage > totalPages) {
      return totalPages;
    }
    if (currentPage < 1) {
      return 1;
    }
    return currentPage;
  }, [currentPage, totalPages]);

  // Slice data for current page
  const paginatedData = useMemo(() => {
    const startIndex = (validCurrentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  }, [data, validCurrentPage, itemsPerPage]);

  // Navigation helpers
  const hasNextPage = validCurrentPage < totalPages;
  const hasPreviousPage = validCurrentPage > 1;

  const goToPage = useCallback(
    (page: number) => {
      const targetPage = Math.max(1, Math.min(page, totalPages));
      setCurrentPage(targetPage);
    },
    [totalPages],
  );

  const nextPage = useCallback(() => {
    if (hasNextPage) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [hasNextPage]);

  const previousPage = useCallback(() => {
    if (hasPreviousPage) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [hasPreviousPage]);

  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  return {
    currentPage: validCurrentPage,
    totalPages,
    totalItems,
    paginatedData,
    itemsPerPage,
    hasNextPage,
    hasPreviousPage,
    goToPage,
    nextPage,
    previousPage,
    resetPage,
  };
};
